import React, { useEffect, useRef } from 'react';
import { usePrimeAuthSession } from '@skyslope/auth-react';
// eslint-disable-next-line import/no-named-as-default -- posthog-js re-exports its own default as a named `posthog` too
import posthog from 'posthog-js';
import useChameleon from '../hooks/useChameleon';
import useHeap from '../hooks/useHeap';
import { isPostHogConfigured, resetPostHog } from '../lib/posthog';
import useUserInfo from './useUserInfo';
import { isWidgetTokenSession } from './widget-token';

// Prime's keep-alive signs in through a hidden iframe, which a cookie-blocked widget frame refuses (it
// retries hourly). Skip it in a widget token session.
function KeepPrimeSessionAlive() {
  usePrimeAuthSession();
  return null;
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default function SecurityEffects() {
  const userInfo = useUserInfo();
  const identifiedUserId = useRef<string | null>(null);

  useHeap();
  useChameleon();

  useEffect(() => {
    if (!userInfo?.senderId) {
      identifiedUserId.current = null;
      return;
    }

    if (!isPostHogConfigured || identifiedUserId.current === userInfo.senderId) {
      return;
    }

    if (identifiedUserId.current && identifiedUserId.current !== userInfo.senderId) {
      resetPostHog();
    }

    const name = [userInfo.senderFirstName, userInfo.senderLastName].filter(Boolean).join(' ');
    posthog.identify(userInfo.senderId, {
      ...(userInfo.senderEmail && { email: userInfo.senderEmail }),
      ...(name && { name }),
      ...(userInfo.primeContactType && { prime_contact_type: userInfo.primeContactType }),
      ...(userInfo.subscriberId && { subscriber_id: userInfo.subscriberId }),
      is_prime_authenticated: userInfo.isPrimeAuthenticated,
    });
    identifiedUserId.current = userInfo.senderId;
  }, [userInfo]);

  return isWidgetTokenSession() ? null : <KeepPrimeSessionAlive />;
}
